import { observable, computed, set } from 'mobx';
import { IOnboardingSequence, IOnboardingStep } from '~/types/server.types';
import { ISetupGuideAdvancedStep } from '~/components/HomePage/ClientFlowSetupGuide/useClientFlowSetupGuide';
import { getMwebSetupGuideStepData } from '~/components/HomePage/Mweb/MwebSetupGuide/mweb-setup-guide.utils';

export class OnboardingSequence<T extends IOnboardingStep = IOnboardingStep>
    implements IOnboardingSequence<T>
{
    @observable steps: T[];
    @observable current_step: T;
    @observable is_sequence_complete: boolean;
    @observable completion_percentage: number;
    @observable is_contract_setup_guide: boolean;
    @observable is_nev_contract_setup_guide: boolean;
    @observable is_jan_jumpstart_campaign: boolean;
    @observable should_hide_setup_guide_from_old_users: boolean;
    @observable should_show_jan_jumpstart_setup_guide: boolean;
    private readonly isAISetupGuideEnabled: boolean;

    constructor(sequence: IOnboardingSequence, isAISetupGuideEnabled = false) {
        set(this, sequence);
        this.isAISetupGuideEnabled = isAISetupGuideEnabled;
    }

    @computed
    get isOnboardingComplete() {
        return this.getPhaseSteps('wizard')?.every(this.isStepComplete);
    }

    @computed
    get setupGuideSteps() {
        return this.isAISetupGuideEnabled
            ? this.steps
            : this.getPhaseSteps('list');
    }

    @computed
    get isSetupGuideComplete() {
        return this.setupGuideSteps?.every(this.isStepComplete);
    }

    @computed
    get isMwebSetupGuideComplete() {
        const mwebSetupGuideStepsKeys = getMwebSetupGuideStepData(
            undefined,
            this.is_jan_jumpstart_campaign
        ).map(step => step.key);
        return this.setupGuideSteps
            ?.filter(step => mwebSetupGuideStepsKeys.includes(step.key as any))
            .every(this.isStepComplete);
    }

    @computed
    get completedSetupGuideSteps() {
        return this.setupGuideSteps?.filter(this.isStepComplete);
    }

    @computed
    get completedSetupGuideStepKeys() {
        return this.completedSetupGuideSteps?.map(step => step.key);
    }

    @computed
    get completedSetupGuideStepsCount() {
        return this.completedSetupGuideSteps?.length || 0;
    }

    @computed
    get setupGuideStepKeys() {
        return this.setupGuideSteps?.map(step => step.key);
    }

    @computed
    get setupGuideStepsCount() {
        return this.setupGuideSteps?.length || 0;
    }

    // arrow function to bind the context
    getSetupGuideStepsToDisplay = (steps: ISetupGuideAdvancedStep[]) => {
        return (
            steps
                .filter(step => this.setupGuideStepKeys?.includes(step.key))
                // Sort the steps by the order as we get them from the server
                .sort((a, b) => {
                    const aOrder = this.setupGuideSteps.find(
                        step => step.key === a.key
                    ).order;
                    const bOrder = this.setupGuideSteps.find(
                        step => step.key === b.key
                    ).order;
                    return aOrder - bOrder;
                })
        );
    };

    private getPhaseSteps(phase: IOnboardingStep['phase']): T[] {
        return this.steps?.filter(step => step.phase === phase);
    }

    private isStepComplete(step: T) {
        return step.status === 'complete';
    }
}
